window.Foxtheme = window.Foxtheme || {};
const PUB_SUB_EVENTS = { cartError: "cart-error" };

const generateDomFromString = (value, tagName = "div") => {
  const d = document.createElement(tagName);
  d.innerHTML = value;
  return d;
};

const getScrollbarWidth = () => {
  const outer = Object.assign(document.createElement("div"), { style: { visibility: "hidden", overflow: "scroll" } });
  document.body.appendChild(outer);
  const inner = document.createElement("div");
  outer.appendChild(inner);
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
  document.body.removeChild(outer);
  return scrollbarWidth;
};

const sectionCache = new Map();
const fetchSection = (sectionId, { url = window.location.href, fromCache = false, params = {} } = {}) => new Promise((resolve, reject) => {
  const _url = new URL(url);
  _url.searchParams.set("section_id", sectionId);
  Object.entries(params).forEach(([k, v]) => _url.searchParams.set(k, v));
  if (fromCache && sectionCache.has(_url)) return resolve(sectionCache.get(_url));
  fetch(_url, { headers: { "X-Requested-With": "XMLHttpRequest", "Content-Type": "application/json" } })
    .then(res => res.ok ? res.text() : reject(`Failed to load section: ${sectionId}`))
    .then(html => { const div = generateDomFromString(html); sectionCache.set(_url, div); resolve(div); })
    .catch(reject);
});

const loadAssets = (files = [], id = Math.random().toString(36).slice(2), callback = () => {}) => {
  if (!window.FoxTheme.loadjs.isDefined(id)) window.FoxTheme.loadjs(files, id);
  window.FoxTheme.loadjs.ready(id, callback);
};

const queryDomNodes = (selectors = {}, context = document) => Object.entries(selectors).reduce((acc, [name, selector]) => {
  const findOne = typeof selector === "string";
  acc[name] = context[findOne ? "querySelector" : "querySelectorAll"](findOne ? selector : selector[0]);
  if (!findOne && acc[name]) acc[name] = [...acc[name]];
  return acc;
}, {});

const addEventDelegate = ({ context = document.documentElement, event = "click", selector, handler, capture = false }) => {
  const listener = e => { for (let target = e.target; target && target !== context; target = target.parentNode) if (target.matches(selector)) { handler.call(target, e, target); break; } };
  context.addEventListener(event, listener, capture);
  return () => context.removeEventListener(event, listener, capture);
};

const moneyFormat = "${{amount}}";
const formatMoney = (cents, format = moneyFormat) => {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatType = format.match(placeholderRegex)[1];
  const formatWithDelimiters = (number, precision = 2, thousands = ",", decimal = ".") => {
    number = (number / 100).toFixed(precision);
    const [dollarsAmount, centsAmount] = number.split(".");
    return dollarsAmount.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (centsAmount ? decimal + centsAmount : "");
  };
  const formattedValue = formatType === "amount" ? formatWithDelimiters(cents, 2) :
    formatType === "amount_no_decimals" ? formatWithDelimiters(cents, 0) :
    formatType === "amount_with_comma_separator" ? formatWithDelimiters(cents, 2, ".", ",") :
    formatWithDelimiters(cents, 0, ".", ",");
  return format.replace(placeholderRegex, formattedValue);
};

const intersecting = ({ element, callback, threshold = 0 }) => {
  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => { if (entry.isIntersecting && typeof callback === "function") { callback(); observer.unobserve(entry.target); } });
  }, { rootMargin: `0px 0px ${threshold}px 0px` });
  observer.observe(element);
};

class AnimateComponent extends HTMLElement {
  constructor() {
    super();
    intersecting({ element: this, callback: this.init.bind(this), threshold: 0 });
  }
  init() {
    this.setAttribute("is-visible", true);
  }
}
customElements.define("animate-component", AnimateComponent);

const getFocusableElements = container => [...container.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe")];

const trapFocus = (container, elementToFocus = container) => {
  const elements = getFocusableElements(container);
  const [first, last] = [elements[0], elements[elements.length - 1]];
  const focusinHandler = event => { if (event.target !== container && event.target !== last && event.target !== first) document.addEventListener("keydown", keydownHandler); };
  const focusoutHandler = () => document.removeEventListener("keydown", keydownHandler);
  const keydownHandler = event => {
    if (event.code.toUpperCase() === "TAB") {
      if (event.target === last && !event.shiftKey) { event.preventDefault(); first.focus(); }
      else if ((event.target === container || event.target === first) && event.shiftKey) { event.preventDefault(); last.focus(); }
    }
  };
  document.addEventListener("focusout", focusoutHandler);
  document.addEventListener("focusin", focusinHandler);
  elementToFocus.focus();
};

const removeTrapFocus = elementToFocus => {
  document.removeEventListener("focusin", focusinHandler);
  document.removeEventListener("focusout", focusoutHandler);
  document.removeEventListener("keydown", keydownHandler);
  if (elementToFocus) elementToFocus.focus();
};

const onKeyUpEscape = event => {
  if (event.code.toUpperCase() !== "ESCAPE") return;
  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
};

const debounce = (fn, wait) => {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
};

const cookiesEnabled = () => navigator.cookieEnabled || (document.cookie = "testcookie", document.cookie.indexOf("testcookie") !== -1);

const setCookie = (cname, cvalue, exdays) => {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  document.cookie = `${cname}=${cvalue};expires=${d.toUTCString()};path=/`;
};

const getCookie = cname => {
  const name = `${cname}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let c of ca) {
    while (c.charAt(0) === " ") c = c.substring(1);
    if (c.indexOf(name) === 0) return c.substring(name.length, c.length);
  }
  return "";
};

const getSrcset = (src, sizes = ["360", "533", "720", "940", "1066"]) => sizes.map(size => `${getSizedImageUrl(src, `${size}x`)} ${size}w`).join(", ");

const removeProtocol = path => path.replace(/http(s)?:/, "");

const getSizedImageUrl = (src, size) => {
  if (!size) return src;
  if (size === "master") return removeProtocol(src);
  const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);
  if (match) {
    const [prefix, suffix] = src.split(match[0]);
    return removeProtocol(`${prefix}_${size}${suffix}`);
  }
  return null;
};

Shopify = Shopify || {};
Shopify.bind = (fn, scope) => () => fn.apply(scope, arguments);
Shopify.setSelectorByValue = (selector, value) => {
  for (let i = 0, count = selector.options.length; i < count; i++) {
    const option = selector.options[i];
    if (value === option.value || value === option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};
Shopify.addListener = (target, eventName, callback) => {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent("on" + eventName, callback);
};
Shopify.postLink = (path, options = {}) => {
  const form = Object.assign(document.createElement("form"), { method: options.method || "post", action: path });
  Object.entries(options.parameters || {}).forEach(([key, value]) => {
    const hiddenField = Object.assign(document.createElement("input"), { type: "hidden", name: key, value });
    form.appendChild(hiddenField);
  });
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options.hideElement || province_domid);
  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));
  this.initCountry();
  this.initProvince();
};
Shopify.CountryProvinceSelector.prototype = {
  initCountry() {
    const value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },
  initProvince() {
    const value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) Shopify.setSelectorByValue(this.provinceEl, value);
  },
  countryHandler() {
    const opt = this.countryEl.options[this.countryEl.selectedIndex];
    const provinces = JSON.parse(opt.getAttribute("data-provinces"));
    this.clearOptions(this.provinceEl);
    if (provinces.length === 0) this.provinceContainer.style.display = "none";
    else {
      provinces.forEach(([value, text]) => {
        const opt = Object.assign(document.createElement("option"), { value, innerHTML: text });
        this.provinceEl.appendChild(opt);
      });
      this.provinceContainer.style.display = "";
    }
  },
  clearOptions(selector) {
    while (selector.firstChild) selector.removeChild(selector.firstChild);
  }
};

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }
  connectedCallback() {
    const { sliderColumns, enableSlider } = this.dataset;
    if (enableSlider === "true") {
      if (this.classList.contains("f-recently-viewed-flickity")) this.initSlider();
      else if (this.childElementCount > parseInt(sliderColumns)) this.initSlider();
      else if (this.childElementCount <= parseInt(sliderColumns)) this.classList.remove("md:flickity-enable");
    }
  }
  initSlider() {
    this.slider = new window.FoxTheme.Slider(this);
    this.disablePointerMove = this.dataset.disablePointerMove || true;
    this.paginateCounterWrapper = this.querySelector(".flickity-page-counter");
    if (this.slider && this.slider.instance && this.dataset.disablePointerMove !== "false") {
      this.classList.add("f-flickity-loaded");
      this.slider.instance.on("pointerMove", () => this.classList.add("is-pointer-move"));
      this.slider.instance.on("pointerUp", () => this.classList.remove("is-pointer-move"));
    }
    if (this.slider && this.slider.instance && this.paginateCounterWrapper) {
      if (this.classList.contains("md:flickity-enable")) {
        this.paginateCounterWrapper.classList.remove("md:hidden");
        this.paginateCounterWrapper.classList.add("md:inline-flex", "hidden");
      } else {
        this.paginateCounterWrapper.classList.remove("hidden");
        this.paginateCounterWrapper.classList.add("inline-flex");
      }
      this.slider.instance.on("change", this.handlePaginateCounter.bind(this));
    }
    if (this.slider && this.slider.instance) {
      this.initialized = true;
      window.onresize = () => {
        const flickityViewPort = this.querySelector(".flickity-viewport");
        if (flickityViewPort) flickityViewPort.style.height = null;
        this.update();
      };
      setTimeout(() => window.dispatchEvent(new Event("resize")), 100);
    }
    if (this.classList.contains("f-recently-viewed-flickity") && this.childElementCount <= parseInt(this.dataset.sliderColumns)) this.classList.remove("md:flickity-enable");
  }
  update() {
    if (this.slider && this.slider.instance) this.slider.instance.resize();
  }
  destroy() {
    if (typeof this.slider.instance === "object") {
      this.slider.instance.destroy();
      this.classList.add("flickity-destroyed");
      this.slider.instance.off("pointerMove");
      this.slider.instance.off("pointerUp");
    }
  }
  select(index, isWrapped = false, isInstant = false) {
    if (this.slider && this.slider.instance) this.slider.instance.select(index, isWrapped, isInstant);
  }
  toggleFade(isFade = false) {
    this.slider.instance.options.fade = isFade;
    this.classList[isFade ? "add" : "remove"]("is-fade");
    this.update();
  }
  handlePaginateCounter(index) {
    const sliderCounterCurrent = this.querySelector(".flickity-counter--current");
    if (sliderCounterCurrent) sliderCounterCurrent.textContent = index + 1;
  }
}
customElements.define("flickity-component", SliderComponent);

class PressNavComponent extends SliderComponent {
  constructor() {
    super();
    document.addEventListener("matchMobile", () => this.slider.instance.resize());
    document.addEventListener("unmatchMobile", () => this.slider.instance.resize());
  }
}
customElements.define("press-nav-component", PressNavComponent);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (poster) poster.addEventListener("click", this.loadContent.bind(this));
    else this.addObserver();
  }
  addObserver() {
    if (!("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadContent(false, false, "observer");
          observer.unobserve(this);
        }
      });
    }, { rootMargin: "0px 0px 0px 0px" });
    observer.observe(this);
  }
  loadContent(focus = true, pause = false, loadTrigger = "click") {
    if (pause) window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      this.loadTrigger = loadTrigger;
      const content = document.createElement("div");
      content.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));
      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(content.querySelector("video, model-viewer, iframe"));
      if (deferredElement && deferredElement.closest(".product-card")) deferredElement.play();
      if (focus) deferredElement.focus();
    }
  }
}
customElements.define("deferred-media", DeferredMedia);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.closeEvent = new Event("close");
    this.openEvent = new Event("open");
    const closeButton = this.querySelector('[id^="ModalClose-"]');
    if (closeButton) closeButton.addEventListener("click", this.hide.bind(this));
    this.addEventListener("keyup", e => { if (e.code.toUpperCase() === "ESCAPE") this.hide(); });
    if (!this.classList.contains("media-modal")) this.addEventListener("click", e => { if (e.target.nodeName === "MODAL-DIALOG") this.hide(); });
    this.scrollbarWidth = getScrollbarWidth();
  }
  connectedCallback() {
    if (!this.moved) {
      this.moved = true;
      document.body.appendChild(this);
    }
  }
  show(opener) {
    const popup = this.querySelector(".template-popup");
    this.openedBy = opener;
    document.body.classList.add("prevent-scroll");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
    this.dispatchEvent(this.openEvent);
    document.body.style.paddingRight = `${this.scrollbarWidth}px`;
  }
  hide() {
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
    this.setAttribute("closing", "true");
    setTimeout(() => {
      this.removeAttribute("open");
      this.removeAttribute("closing");
      document.body.classList.remove("prevent-scroll");
      this.dispatchEvent(this.closeEvent);
      document.body.style.paddingRight = "";
    }, 300);
  }
}
customElements.define("modal-dialog", ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector("button");
    if (button) button.addEventListener("click", () => {
      const modal = document.querySelector(this.dataset.modal);
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.init();
  }
  init() {
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.productId = this.dataset.productId;
    this.querySelectorAll("button").forEach(button => button.addEventListener("click", this.onButtonClick.bind(this)));
    this.input.addEventListener("change", () => {
      window.FoxThemeEvents.emit(`${this.productId}__QUANTITY_CHANGE`, this.input.value, this);
    });
  }
  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}
customElements.define("quantity-input", QuantityInput);

class Drawer extends HTMLElement {
  constructor() {
    super();
    this.closeEvent = new Event("close");
    this.openEvent = new Event("open");
    this.opened = false;
    const closeButton = this.querySelector('[id^="DrawerClose-"]');
    if (closeButton) closeButton.addEventListener("click", e => { e.preventDefault(); this.closeDrawer(); });
    this.addEventListener("keyup", e => { if (e.code.toUpperCase() === "ESCAPE") this.closeDrawer(); });
    this.elementToFocus = this.querySelector('[tabindex="-1"]') || this.querySelector('input:not([type="hidden"])');
    if (this.dataset.elementToFocus) this.elementToFocus = this.querySelector(this.dataset.elementToFocus);
  }
  connectedCallback() {
    if (!this.moved) {
      this.moved = true;
      document.body.appendChild(this);
    }
  }
  isOpen() {
    return this.hasAttribute("open");
  }
  onBodyClick(e) {
    if (e.target.classList.contains("f-drawer__overlay")) this.closeDrawer(false);
  }
  openDrawer(opener) {
    this.openedBy = opener;
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    document.body.addEventListener("click", this.onBodyClickEvent);
    document.body.classList.add("prevent-scroll");
    this.setAttribute("open", "");
    trapFocus(this, this.elementToFocus);
    this.dispatchEvent(this.openEvent);
  }
  closeDrawer(focusToggle = true) {
    if (focusToggle) removeTrapFocus(this.openedBy);
    this.removeAttribute("open");
    document.body.removeEventListener("click", this.onBodyClickEvent);
    document.body.classList.remove("prevent-scroll");
    this.dispatchEvent(this.closeEvent);
  }
}
customElements.define("drawer-component", Drawer);

customElements.define("drawer-opener", class extends HTMLElement {
  constructor() {
    super();
    if (this.dataset.disabled === "true") return;
    const button = this.querySelector("button") || this.querySelector("[data-opener]");
    if (button) button.addEventListener("click", e => {
      e.preventDefault();
      const drawer = document.querySelector(this.dataset.drawer);
      if (drawer) drawer.openDrawer(button);
    });
  }
});

customElements.define("drawer-closer", class extends HTMLElement {
  constructor() {
    super();
    const drawer = this.closest("drawer-component");
    if (drawer) this.addEventListener("click", e => {
      e.preventDefault();
      drawer.closeDrawer();
    });
  }
});

customElements.define("cart-note", class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", debounce(event => {
      const body = JSON.stringify({ note: event.target.value });
      fetch(`${FoxThemeSettings.routes.cart_update_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body
      });
    }, 300));
    this.collapsible = this.querySelector("collapsible-tab");
    this.querySelector(".btn-cancel").addEventListener("click", this.handleClose.bind(this));
  }
  handleClose(e) {
    e.preventDefault();
    if (this.collapsible) this.collapsible.close();
  }
});

customElements.define("cart-delivery-time", class extends HTMLElement {
  constructor() {
    super();
    this.collapsible = this.querySelector("collapsible-tab");
    this.deliveryTimeElm = this.querySelector("[name='attributes[Delivery time]']");
    this.errorWrapper = this.querySelector(".f-cart-block-message-error");
    this.deliveryCodeKey = "zest-delivery-code";
    this.querySelector(".btn-cancel").addEventListener("click", this.handleClose.bind(this));
    this.querySelector(".btn-save").addEventListener("click", this.handleSave.bind(this));
    const today = new Date().toISOString().slice(0, 16);
    if (this.deliveryTimeElm) this.deliveryTimeElm.min = today;
  }
  handleSave(e) {
    e.preventDefault();
    const time = this.deliveryTimeElm.value;
    if (Date.parse(time) > Date.now()) {
      localStorage.setItem(this.deliveryCodeKey, time);
      const body = JSON.stringify({ attributes: { "Delivery time": time } });
      fetch(`${FoxThemeSettings.routes.cart_update_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body
      });
      this.handleClose(e);
    } else if (time === "") {
      localStorage.removeItem(this.deliveryCodeKey);
      window.FoxTheme.Notification.show({
        target: this.errorWrapper,
        method: "appendChild",
        type: "warning",
        message: window.FoxThemeStrings.noSelectDeliveryTime
      });
    } else {
      localStorage.removeItem(this.deliveryCodeKey);
      window.FoxTheme.Notification.show({
        target: this.errorWrapper,
        method: "appendChild",
        type: "warning",
        message: window.FoxThemeStrings.invalidDeliveryTime
      });
    }
  }
  handleClose(e) {
    e.preventDefault();
    if (this.collapsible) this.collapsible.close();
  }
});

customElements.define("product-modal", class extends ModalDialog {
  constructor() {
    super();
  }
  connectedCallback() {
    this.slider = this.querySelector("flickity-component");
    this.mediaContent = this.querySelector(".media-modal__content");
  }
  hide() {
    super.hide();
    this.mediaContent.setAttribute("data-media-loading", "");
  }
  show(opener) {
    super.show(opener);
    if (this.slider) setTimeout(() => {
      this.slider.update();
      this.showActiveMedia();
      this.slider.focus();
    }, 300);
  }
  showActiveMedia() {
    const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`);
    if (activeMedia) {
      const activeIndex = Number(activeMedia.dataset.index);
      this.slider.select(activeIndex, false, true);
      this.mediaContent.removeAttribute("data-media-loading");
    }
  }
});

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content = this.mainDetailsToggle.querySelector("summary").nextElementSibling;
    this.mainDetailsToggle.addEventListener("focusout", this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }
  onFocusOut() {
    setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); });
  }
  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();
    this.mainDetailsToggle.hasAttribute("open") ? this.animations.forEach(animation => animation.play()) : this.animations.forEach(animation => animation.cancel());
  }
  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle.querySelector("summary").setAttribute("aria-expanded", false);
  }
  open() {
    this.mainDetailsToggle.setAttribute("open", "");
    this.mainDetailsToggle.querySelector("summary").setAttribute("aria-expanded", true);
  }
}
customElements.define("details-disclosure", DetailsDisclosure);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (entries[0].isIntersecting) {
        observer.unobserve(this);
        fetch(this.dataset.url)
          .then(response => response.text())
          .then(text => {
            const html = generateDomFromString(text);
            const recommendations = html.querySelector("product-recommendations");
            if (recommendations && recommendations.innerHTML.trim().length) {
              this.innerHTML = recommendations.innerHTML;
              __reInitTooltip(this);
            }
            this.classList.toggle("product-recommendations--loaded", !!html.querySelector(".product-card"));
            if (!html.querySelector(".product-card")) this.classList.add("f-hidden");
          })
          .catch(e => console.error(e));
      }
    };
    new IntersectionObserver(handleIntersection.bind(this), { rootMargin: "0px 0px 400px 0px" }).observe(this);
  }
}
customElements.define("product-recommendations", ProductRecommendations);

class FProgressBar extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.init();
  }
  init() {
    setTimeout(() => {
      const quantity = parseInt(this.dataset.quantity);
      this.style.setProperty("--progress-bar-width", `${quantity * 100 / 50}%`);
    }, 1000);
  }
}
customElements.define("f-progress-bar", FProgressBar);

class VideoComponent extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.init();
  }
  init() {
    this.parentSelector = this.dataset.parent || ".f-video-wrapper";
    this.parent = this.closest(this.parentSelector);
    switch (this.dataset.type) {
      case "youtube":
        this.initYoutubeVideo();
        break;
      case "vimeo":
        this.initVimeoVideo();
        break;
      case "mp4":
        this.initMp4Video();
        break;
    }
  }
  initYoutubeVideo() {
    this.setAsLoading();
    this.loadScript("youtube").then(this.setupYoutubePlayer.bind(this));
  }
  initVimeoVideo() {
    this.setAsLoading();
    this.loadScript("vimeo").then(this.setupVimeoPlayer.bind(this));
  }
  initMp4Video() {
    const player = this.querySelector("video");
    if (player) {
      const promise = player.play();
      if (typeof promise !== "undefined") promise.catch(() => player.setAttribute("controls", ""));
    }
  }
  loadScript(videoType) {
    return new Promise((resolve, reject) => {
      const script = Object.assign(document.createElement("script"), { async: true, onload: resolve, onerror: reject });
      document.body.appendChild(script);
      script.src = videoType === "youtube" ? "//www.youtube.com/iframe_api" : "//player.vimeo.com/api/player.js";
    });
  }
  setAsLoading() {
    this.parent.setAttribute("loading", true);
  }
  setAsLoaded() {
    this.parent.removeAttribute("loading");
    this.parent.setAttribute("loaded", true);
  }
  setupYoutubePlayer() {
    const videoId = this.dataset.videoId;
    const playerInterval = setInterval(() => {
      if (window.YT && window.YT.ready) {
        const element = document.createElement("div");
        this.appendChild(element);
        this.player = new YT.Player(element, {
          videoId,
          playerVars: {
            showinfo: 0,
            controls: false,
            fs: 0,
            rel: 0,
            height: "100%",
            width: "100%",
            iv_load_policy: 3,
            html5: 1,
            loop: 1,
            playsinline: 1,
            modestbranding: 1,
            disablekb: 1
          },
          events: {
            onReady: this.onYoutubeReady.bind(this),
            onStateChange: this.onYoutubeStateChange.bind(this)
          }
        });
        clearInterval(playerInterval);
      }
    }, 50);
  }
  onYoutubeReady() {
    this.iframe = this.querySelector("iframe");
    this.iframe.setAttribute("tabindex", "-1");
    if (typeof this.player.mute === "function") this.player.mute();
    if (typeof this.player.playVideo === "function") this.player.playVideo();
    this.setAsLoaded();
    new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        entry.isIntersecting ? this.youtubePlay() : this.youtubePause();
      });
    }, { rootMargin: "0px 0px 50px 0px" }).observe(this.iframe);
  }
  onYoutubeStateChange(event) {
    if (event.data === -1 && this.attemptedToPlay) this.setAsLoaded();
    if (event.data === 0) this.youtubePlay();
    if (event.data === 1) this.setAsLoaded();
    if (event.data === 3) this.attemptedToPlay = true;
  }
  youtubePlay() {
    if (this.player && typeof this.player.playVideo === "function") this.player.playVideo();
  }
  youtubePause() {
    if (this.player && typeof this.player.pauseVideo === "function") this.player.pauseVideo();
  }
  setupVimeoPlayer() {
    const videoId = this.dataset.videoId;
    const playerInterval = setInterval(() => {
      if (window.Vimeo) {
        this.player = new Vimeo.Player(this, {
          id: videoId,
          autoplay: true,
          autopause: false,
          background: false,
          controls: false,
          loop: true,
          height: "100%",
          width: "100%"
        });
        this.player.ready().then(this.onVimeoReady.bind(this));
        clearInterval(playerInterval);
      }
    }, 50);
  }
  onVimeoReady() {
    this.iframe = this.querySelector("iframe");
    this.iframe.setAttribute("tabindex", "-1");
    this.player.setMuted(true);
    this.setAsLoaded();
    new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        entry.isIntersecting ? this.vimeoPlay() : this.vimeoPause();
      });
    }, { rootMargin: "0px 0px 50px 0px" }).observe(this.iframe);
  }
  vimeoPlay() {
    if (this.player && typeof this.player.play === "function") this.player.play();
  }
  vimeoPause() {
    if (this.player && typeof this.player.pause === "function") this.player.pause();
  }
}
customElements.define("video-component", VideoComponent);
